import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import SlidesPage from "./pages/SlidesPage";
import "./index.css";

// ─── Asset imports ────────────────────────────────────────────────────────────
import deepseekSvg    from "./assets/deepseek-color.svg";
import qwenSvg        from "./assets/qwen-color.svg";
import gemmaWebp      from "./assets/gemma.webp";
import upiSvg         from "./assets/UPI-Color.svg";
import opencodeSvg    from "./assets/opencode.svg";
import openclawSvg    from "./assets/openclaw-color.svg";
import kilocodeSvg    from "./assets/kilocode.svg";
import openwebuiSvg   from "./assets/openwebui.svg";
import zaiSvg         from "./assets/zai-text.svg";

// ─── Types ────────────────────────────────────────────────────────────────────
type IconName =
  | "cube" | "bolt" | "shield" | "rupee" | "spark" | "speed"
  | "grid" | "lock" | "headset" | "arrow-down" | "chevron"
  | "check" | "arrow-right" | "menu" | "x" | "zap" | "globe"
  | "cpu" | "layers" | "terminal" | "database" | "trending-down" | "code2";

// ─── Animation helpers ────────────────────────────────────────────────────────
const ease = [0.25, 0.46, 0.45, 0.94] as const;

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease, delay } },
});

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};

const cardAnim = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease } },
};

function InView({
  children, className = "", variants, amount = 0.12,
}: {
  children: React.ReactNode;
  className?: string;
  variants: ReturnType<typeof fadeUp>;
  amount?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount });
  return (
    <motion.div ref={ref} className={className} variants={variants}
      initial="hidden" animate={inView ? "visible" : "hidden"}>
      {children}
    </motion.div>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const navItems = [
  { label: "Docs", href: "/#docs" },
  { label: "Slides", href: "/slides" },
];

const models = [
  {
    name: "DeepSeek",
    family: "R1 · V2 · Coder",
    detail: "State-of-the-art reasoning and coding. Best-in-class for complex problem solving.",
    chips: ["R1", "V2", "Coder"],
    badge: "Popular",
    logo: deepseekSvg,
    accent: "#4D6BFE",
  },
  {
    name: "Gemma",
    family: "2B · 7B · 9B · 27B",
    detail: "Powerful, efficient open models by Google. Excellent multilingual capabilities.",
    chips: ["2B", "7B", "9B", "27B"],
    badge: "New",
    logo: gemmaWebp,
    accent: "#4285F4",
  },
  {
    name: "Qwen",
    family: "1.5B – 72B",
    detail: "Advanced multilingual models by Alibaba. Excels at code generation and reasoning.",
    chips: ["1.5B", "7B", "14B", "72B"],
    badge: null,
    logo: qwenSvg,
    accent: "#6336E7",
  },
  {
    name: "GLM",
    family: "4 · 4v · Turbo",
    detail: "ChatGLM series by Zhipu AI. Smart, fast, open with strong bilingual capabilities.",
    chips: ["4", "4v", "Turbo"],
    badge: null,
    logo: null,          // inline SVG below
    accent: "#1473FF",
  },
  {
    name: "50+ Models",
    family: "And growing",
    detail: "Llama 3, Mistral, Mixtral, Falcon, Yi and dozens more on a single platform.",
    chips: ["Llama 3", "Mistral", "Mixtral"],
    badge: null,
    logo: null,
    accent: "#0891b2",
  },
];

const stats = [
  { value: "99.9%", label: "Uptime SLA",          icon: "shield"       as IconName },
  { value: "<100ms", label: "P50 Latency",         icon: "speed"        as IconName },
  { value: "2×",    label: "Faster Inference",     icon: "zap"          as IconName },
  { value: "150+",  label: "Models Available",     icon: "layers"       as IconName },
  { value: "100%",  label: "Data in India",        icon: "lock"         as IconName },
  { value: "24/7",  label: "Developer Support",    icon: "headset"      as IconName },
];

const mobileStats = [
  { value: "2×",    label: "Faster Inference",     icon: "zap"          as IconName },
  { value: "<100ms",label: "P50 Latency",          icon: "speed"        as IconName },
  { value: "60%",   label: "Lower Cost",           icon: "trending-down"as IconName },
  { value: "99.9%", label: "Uptime SLA",           icon: "shield"       as IconName },
  { value: "100%",  label: "Data in India",        icon: "lock"         as IconName },
  { value: "24/7",  label: "Support",              icon: "headset"      as IconName },
];

const features = [
  { icon: "terminal"  as IconName, title: "OpenAI-Compatible API",  desc: "Drop-in replacement. Change one line of code to switch from OpenAI or Anthropic." },
  { icon: "globe"     as IconName, title: "Indian Infrastructure",  desc: "All compute runs within India. Your data never leaves — sovereign by design." },
  { icon: "cpu"       as IconName, title: "High-Performance Inference", desc: "Optimised GPU clusters with custom kernels deliver 2× throughput at half the cost." },
  { icon: "database"  as IconName, title: "Pay in INR via UPI",     desc: "No forex. No international cards. Pay exactly what you use, instantly, via UPI." },
];

// IDE/tool compatibility list
const compatTools = [
  { name: "OpenCode",   logo: opencodeSvg,  color: "#ffffff",  desc: "AI coding agent" },
  { name: "OpenClaw",   logo: openclawSvg,  color: "#FF4D4D",  desc: "AI pair programmer" },
  { name: "Kilo Code",  logo: kilocodeSvg,  color: "#ffffff",  desc: "Code generation" },
  { name: "Open WebUI", logo: openwebuiSvg, color: "#ffffff",  desc: "Chat interface" },
  { name: "Z.ai",       logo: zaiSvg,       color: "#ffffff",  desc: "AI workspace" },
];

// ─── Icon ─────────────────────────────────────────────────────────────────────
function Icon({ name, className = "" }: { name: IconName; className?: string }) {
  const base = `w-5 h-5 flex-shrink-0 ${className}`;
  const paths: Partial<Record<IconName, React.ReactNode>> = {
    cube:      <><path d="M12 2.7 20 7.2v9.2l-8 4.9-8-4.9V7.2l8-4.5Z" stroke="currentColor" strokeWidth="1.6"/><path d="m4.5 7.5 7.5 4.4 7.5-4.4M12 12v8.4" stroke="currentColor" strokeWidth="1.6"/></>,
    bolt:      <path d="m13.2 2.5-8 11.1h6.1l-1 7.9 8.5-12h-6.2l.6-7Z" fill="currentColor"/>,
    shield:    <path d="M12 3.2 19 6v5.4c0 4.5-2.9 7.8-7 9.4-4.1-1.6-7-4.9-7-9.4V6l7-2.8Z" stroke="currentColor" strokeWidth="1.6"/>,
    rupee:     <></>,
    spark:     <path d="M12 2.8 14.4 9l6.2 3-6.2 3L12 21.2 9.6 15l-6.2-3 6.2-3L12 2.8Z" fill="currentColor"/>,
    speed:     <><path d="M5 16a8 8 0 1 1 14 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="m12 13 4-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="12" cy="16" r="1" fill="currentColor"/></>,
    grid:      <><rect x="4" y="4" width="6" height="6" rx="1" fill="currentColor" opacity=".8"/><rect x="14" y="4" width="6" height="6" rx="1" fill="currentColor" opacity=".8"/><rect x="4" y="14" width="6" height="6" rx="1" fill="currentColor" opacity=".8"/><rect x="14" y="14" width="6" height="6" rx="1" fill="currentColor" opacity=".8"/></>,
    lock:      <><rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6"/></>,
    headset:   <><path d="M4 13a8 8 0 0 1 16 0v4a3 3 0 0 1-3 3h-2" stroke="currentColor" strokeWidth="1.6"/><rect x="3" y="12" width="4" height="6" rx="2" stroke="currentColor" strokeWidth="1.6"/><rect x="17" y="12" width="4" height="6" rx="2" stroke="currentColor" strokeWidth="1.6"/></>,
    "arrow-down": <path d="M12 5v14m0 0 6-6m-6 6-6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>,
    chevron:   <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>,
    check:     <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>,
    "arrow-right": <path d="M5 12h14m0 0-6-6m6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>,
    menu:      <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>,
    x:         <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>,
    zap:       <path d="m13 2-8 11h7l-1 9 9-13h-7l.6-7z" fill="currentColor"/>,
    globe:     <><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/><path d="M12 3c-2 2.5-3 5.5-3 9s1 6.5 3 9M12 3c2 2.5 3 5.5 3 9s-1 6.5-3 9M3 12h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></>,
    cpu:       <><rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.6"/><rect x="9" y="9" width="6" height="6" stroke="currentColor" strokeWidth="1.4"/><path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></>,
    layers:    <path d="M2 7l10-5 10 5-10 5L2 7zm0 5 10 5 10-5M2 17l10 5 10-5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>,
    terminal:  <><rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="m8 9 3 3-3 3M13 15h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></>,
    database:  <><ellipse cx="12" cy="6" rx="8" ry="3" stroke="currentColor" strokeWidth="1.6"/><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" stroke="currentColor" strokeWidth="1.6"/><path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" stroke="currentColor" strokeWidth="1.6"/></>,
    "trending-down": <><path d="m23 18-9.5-9.5-5 5L2 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 18h7v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></>,
    code2:     <path d="m8 9-3 3 3 3m8-6 3 3-3 3M14 5l-4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>,
  };
  if (name === "rupee") return <span className={`font-black leading-none ${className}`}>₹</span>;
  return (
    <svg className={base} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

// ─── Model Logo ───────────────────────────────────────────────────────────────
function ModelLogo({ model, size = "md" }: { model: typeof models[number]; size?: "sm" | "md" | "lg" }) {
  const px = size === "lg" ? 56 : size === "sm" ? 32 : 44;
  const cls = `rounded-xl object-contain flex-shrink-0`;

  // GLM inline SVG (no file in dev-content)
  if (model.name === "GLM") {
    return (
      <svg width={px} height={px} viewBox="0 0 48 48" fill="none" className="flex-shrink-0 rounded-xl" aria-label="GLM">
        <rect width="48" height="48" rx="10" fill="#0a1628"/>
        <circle cx="24" cy="24" r="14" stroke="#1473FF" strokeWidth="3" fill="none" opacity="0.9"/>
        <circle cx="24" cy="24" r="9"  stroke="#1473FF" strokeWidth="2.5" fill="none" opacity="0.6"/>
        <circle cx="24" cy="24" r="4"  fill="#1473FF"/>
        <line x1="24" y1="6"  x2="24" y2="10" stroke="#1473FF" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="24" y1="38" x2="24" y2="42" stroke="#1473FF" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="6"  y1="24" x2="10" y2="24" stroke="#1473FF" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="38" y1="24" x2="42" y2="24" stroke="#1473FF" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    );
  }

  // 50+ models placeholder
  if (!model.logo) {
    return (
      <svg width={px} height={px} viewBox="0 0 48 48" fill="none" className="flex-shrink-0 rounded-xl" aria-label="More models">
        <rect width="48" height="48" rx="10" fill="#0f172a" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
        <rect x="10" y="10" width="11" height="11" rx="2.5" fill="rgba(6,182,212,0.7)"/>
        <rect x="27" y="10" width="11" height="11" rx="2.5" fill="rgba(6,182,212,0.5)"/>
        <rect x="10" y="27" width="11" height="11" rx="2.5" fill="rgba(6,182,212,0.5)"/>
        <rect x="27" y="27" width="11" height="11" rx="2.5" fill="rgba(6,182,212,0.3)"/>
      </svg>
    );
  }

  return (
    <img
      src={model.logo}
      alt={model.name}
      width={px}
      height={px}
      className={cls}
      style={{ background: "transparent" }}
    />
  );
}

// ─── BrandMark ────────────────────────────────────────────────────────────────
function BrandMark() {
  return (
    <a href="/" className="flex items-center gap-2.5">
      <div className="relative h-8 w-8 overflow-hidden rounded-lg bg-gradient-to-br from-[#818cf8] via-[#6366f1] to-[#4338ca] shadow-[0_0_18px_rgba(99,102,241,.4)] flex-shrink-0">
        <span className="absolute left-[5px] top-[-1px] text-[30px] font-black leading-none tracking-[-.2em] text-white/95">K</span>
        <span className="absolute inset-y-0 right-1 w-2 rotate-45 rounded-sm bg-white/20 blur-[1px]" />
      </div>
      <span className="text-[1.05rem] font-semibold tracking-[-0.03em] text-white">Krato AI</span>
    </a>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <motion.header
      className="fixed top-0 inset-x-0 z-50 border-b border-white/[0.06] bg-[#0d0d1e]/85 backdrop-blur-xl"
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.45, ease }}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-5 lg:px-8">
        <BrandMark />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <a key={item.label} href={item.href}
              className="rounded-md px-3 py-2 text-[13px] font-medium text-white/60 transition-colors hover:bg-white/[0.05] hover:text-white/90">
              {item.label}
            </a>
          ))}
        </nav>

        {/* Mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          <button onClick={() => setOpen(!open)}
            className="grid h-8 w-8 place-items-center rounded-lg border border-white/[0.1] text-white/60"
            aria-label="Toggle menu">
            <Icon name={open ? "x" : "menu"} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="border-t border-white/[0.06] bg-[#0d0d1e]/95 backdrop-blur-xl lg:hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease }}
          >
            <div className="flex flex-col gap-0.5 px-4 py-3">
              {navItems.map((item) => (
                <a key={item.label} href={item.href} onClick={() => setOpen(false)}
                  className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-white/65 hover:bg-white/[0.05] hover:text-white">
                  {item.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

// ─── Announcement Bar ─────────────────────────────────────────────────────────
function AnnouncementBar() {
  return (
    <div className="fixed top-14 inset-x-0 z-40 hidden border-b border-white/[0.05] bg-[#0d0d1e]/70 backdrop-blur-md lg:block">
      <div className="mx-auto flex h-9 max-w-7xl items-center justify-center gap-2.5 px-8 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-cyan-400/[0.08] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-cyan-400">
          New
        </span>
        <span className="text-white/50">Indian AI Infrastructure — Open Models, Developer Freedom.</span>
        <a href="#learn" className="flex items-center gap-1 font-medium text-white/75 transition hover:text-white">
          Learn more <Icon name="arrow-right" className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative flex items-center overflow-hidden hero-section">
      {/* Background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="hero-orb-1 absolute -top-32 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-full" />
        <div className="hero-orb-2 absolute top-1/2 left-1/5 h-[320px] w-[320px] -translate-y-1/2 rounded-full blur-3xl" />
        <div className="hero-orb-3 absolute top-1/3 right-1/6 h-[240px] w-[240px] rounded-full blur-3xl" />
        <div className="hero-grid absolute inset-0" />
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-[#0a0a19] to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_0.92fr] lg:gap-10 xl:gap-14">

          {/* Left: Text */}
          <div className="max-w-xl">
            <motion.div
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/[0.09] bg-white/[0.04] px-3.5 py-1.5 text-xs"
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1, ease }}>
              <span className="text-sm">🇮🇳</span>
              <span className="text-white/65 font-medium tracking-wide">Built in India. For the world.</span>
            </motion.div>

            <motion.h1
              className="mb-5 text-[clamp(2rem,3.8vw,3.2rem)] font-semibold leading-[1.1] tracking-[-0.04em] text-white"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.16, ease }}>
              Open AI infrastructure<br />
              <span className="hero-headline-accent">powered by open source.</span>
            </motion.h1>

            <motion.p
              className="mb-7 max-w-[480px] text-[0.95rem] leading-[1.75] text-white/45"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.26, ease }}>
              Run the world's best open-weight models with OpenAI &amp; Anthropic compatible APIs.
              Pay in INR via UPI. Keep your data in India.{" "}
              <span className="text-white/65">Simple. Sovereign. Scalable.</span>
            </motion.p>

            <motion.div
              className="flex flex-wrap items-center gap-2.5 mb-8"
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.34, ease }}>
              <a href="#models"
                className="hero-primary-btn group inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-all">
                Start Building
                <Icon name="arrow-right" className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a href="#docs"
                className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] bg-white/[0.03] px-5 py-2.5 text-sm font-medium text-white/70 transition-all hover:border-white/20 hover:bg-white/[0.06] hover:text-white">
                <Icon name="terminal" className="w-3.5 h-3.5 opacity-60" />
                View API Docs
              </a>
            </motion.div>

            {/* Feature pills */}
            <motion.div
              className="flex flex-wrap gap-x-5 gap-y-2.5"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.44, ease }}>
              {[
                { icon: "cube"   as IconName, label: "Open Source Models" },
                { icon: "bolt"   as IconName, label: "High Performance" },
                { icon: "shield" as IconName, label: "Enterprise Grade" },
                { icon: "rupee"  as IconName, label: "Pay in INR via UPI" },
              ].map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-1.5 text-xs text-white/38">
                  <Icon name={icon} className="w-3.5 h-3.5 text-cyan-400/65" />
                  <span>{label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Visual Card */}
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

// ─── Hero Visual ──────────────────────────────────────────────────────────────
function HeroVisual() {
  return (
    <div className="relative hidden lg:block">
      <motion.div
        className="relative"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.28, ease }}>

        {/* Main card */}
        <div className="hero-visual-card relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#111127]/80 p-5 backdrop-blur-xl">
          {/* Traffic-light dots + URL bar */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-red-500/60" />
              <div className="h-2 w-2 rounded-full bg-yellow-500/60" />
              <div className="h-2 w-2 rounded-full bg-green-500/60" />
            </div>
            <div className="flex items-center gap-1.5 rounded-md border border-white/[0.06] bg-white/[0.03] px-2.5 py-1 text-[11px] text-white/35">
              <Icon name="terminal" className="w-3 h-3" />
              api.krato.ai/v1
            </div>
          </div>

          {/* Code block */}
          <div className="mb-4 rounded-xl border border-white/[0.05] bg-[#0a0a18] px-4 py-3">
            <pre className="text-[11px] leading-[1.7] text-white/65 overflow-x-auto">
              <code>
                <span className="text-blue-400/75">from</span>
                <span className="text-white/45"> openai </span>
                <span className="text-blue-400/75">import</span>
                <span className="text-white/75"> OpenAI{"\n\n"}</span>
                <span className="text-white/45">client = </span>
                <span className="text-cyan-400/85">OpenAI</span>
                <span className="text-white/45">({"\n"}</span>
                <span className="text-white/45">{"  "}base_url=</span>
                <span className="text-emerald-400/80">"https://api.krato.ai/v1"</span>
                <span className="text-white/45">,{"\n"}</span>
                <span className="text-white/45">{"  "}api_key=</span>
                <span className="text-emerald-400/80">"kr-••••••••"</span>
                <span className="text-white/45">,{"\n"}){"\n\n"}</span>
                <span className="text-white/45">response = client.chat.completions.</span>
                <span className="text-cyan-400/85">create</span>
                <span className="text-white/45">({"\n"}</span>
                <span className="text-white/45">{"  "}model=</span>
                <span className="text-emerald-400/80">"deepseek-r1"</span>
                <span className="text-white/45">,{"\n"}</span>
                <span className="text-white/45">{"  "}messages=[...]</span>
                <span className="text-white/45">{"\n"})</span>
              </code>
            </pre>
          </div>

          {/* Model list */}
          <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-widest text-white/25">
            Available Models
          </p>
          <div className="flex flex-col gap-1.5">
            {models.slice(0, 4).map((model) => (
              <motion.div key={model.name}
                className="flex items-center gap-2.5 rounded-lg border border-white/[0.04] bg-white/[0.02] px-3 py-2.5 cursor-pointer"
                whileHover={{ borderColor: "rgba(6,182,212,0.18)", backgroundColor: "rgba(6,182,212,0.035)" }}
                transition={{ duration: 0.15 }}>
                <ModelLogo model={model} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-white/82">{model.name}</p>
                  <p className="text-[11px] text-white/32">{model.family}</p>
                </div>
                {model.badge && (
                  <span className="rounded-md bg-cyan-400/[0.1] px-1.5 py-0.5 text-[10px] font-semibold text-cyan-400">
                    {model.badge}
                  </span>
                )}
                <Icon name="arrow-right" className="w-3 h-3 text-white/18" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Floating UPI badge */}
        <motion.div
          className="absolute -bottom-5 -right-5 rounded-2xl border border-white/[0.09] bg-[#111127]/92 px-4 py-3.5 backdrop-blur-xl shadow-2xl"
          initial={{ opacity: 0, y: 18, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.8, ease }}
          style={{ animation: "float 4s ease-in-out infinite" }}>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full border border-violet-400/25 text-xl font-black text-violet-300">₹</div>
            <div>
              <p className="text-[1.25rem] font-black leading-none text-white">₹100</p>
              <p className="text-[11px] text-white/40 mt-0.5">per 1M tokens</p>
            </div>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-white/[0.06]">
            <img src={upiSvg} alt="UPI" className="h-6 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
          </div>
        </motion.div>

        {/* Floating latency badge */}
        <motion.div
          className="absolute -top-4 -left-5 rounded-xl border border-white/[0.07] bg-[#111127]/92 px-3.5 py-2.5 backdrop-blur-xl shadow-xl"
          initial={{ opacity: 0, y: -14, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.95, ease }}
          style={{ animation: "float 3.6s 0.5s ease-in-out infinite" }}>
          <p className="text-[11px] text-white/38 mb-0.5">P50 Latency</p>
          <p className="text-[1.1rem] font-black text-cyan-400">&lt;100ms</p>
        </motion.div>
      </motion.div>
    </div>
  );
}

// ─── UPI Pricing Bar ──────────────────────────────────────────────────────────
function PricingBar() {
  return (
    <section className="relative py-5">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <InView variants={fadeUp(0.05)}>
          <div className="rounded-2xl border border-white/[0.07] bg-[#111127]/55 p-5 backdrop-blur-sm lg:p-6">
            <div className="grid items-center gap-5 lg:grid-cols-[1.5fr_1fr_1fr_auto_1fr] lg:divide-x lg:divide-white/[0.07]">

              <div className="flex items-center gap-3.5">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-violet-400/22 bg-violet-400/[0.07] text-xl font-black text-violet-300">₹</div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Pay in Rupees via UPI</h2>
                  <p className="mt-0.5 text-xs text-white/40">No forex. No international cards. Instant activation.</p>
                </div>
              </div>

              <div className="lg:pl-7">
                <p className="text-xl font-black text-white">₹100 <span className="text-sm font-medium text-white/38">/ 1M tokens</span></p>
                <p className="mt-1 text-xs text-white/40">Input Tokens</p>
              </div>

              <div className="lg:pl-7">
                <p className="text-xl font-black text-white">₹100 <span className="text-sm font-medium text-white/38">/ 1M tokens</span></p>
                <p className="mt-1 text-xs text-white/40">Output Tokens</p>
              </div>

              <div className="hidden lg:flex items-center justify-center px-7">
                <img src={upiSvg} alt="UPI – Unified Payments Interface" className="h-10 w-auto"
                  style={{ filter: "brightness(0) invert(1)", opacity: 0.75 }} />
              </div>

              <div className="space-y-2 lg:pl-7">
                {["UPI, Cards, Net Banking", "Transparent billing", "Cancel anytime"].map((item) => (
                  <p key={item} className="flex items-center gap-2 text-xs text-white/50">
                    <Icon name="check" className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </InView>
      </div>
    </section>
  );
}

// ─── Features Section ─────────────────────────────────────────────────────────
function FeaturesSection() {
  return (
    <section id="docs" className="py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <InView variants={fadeUp(0)} className="mb-12 max-w-lg">
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-widest text-cyan-400/75">Why Krato AI</p>
          <h2 className="text-[clamp(1.6rem,3vw,2.4rem)] font-semibold leading-[1.15] tracking-[-0.03em] text-white">
            Everything you need to build with open AI
          </h2>
          <p className="mt-3 text-sm text-white/40 leading-relaxed">
            A full-stack AI platform purpose-built for Indian developers and enterprises.
          </p>
        </InView>

        <motion.div
          className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4"
          variants={stagger} initial="hidden"
          whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
          {features.map((f) => (
            <motion.div key={f.title}
              className="feature-card group relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#111127]/50 p-5 transition-all duration-300 hover:border-white/[0.12] hover:bg-[#111127]/75"
              variants={cardAnim} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <div className="mb-4 inline-flex items-center justify-center rounded-xl border border-white/[0.07] bg-white/[0.04] p-2.5">
                <Icon name={f.icon} className="w-4.5 h-4.5 text-cyan-400" />
              </div>
              <h3 className="mb-1.5 text-sm font-semibold text-white">{f.title}</h3>
              <p className="text-xs text-white/40 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Models Section ───────────────────────────────────────────────────────────
function ModelsSection() {
  return (
    <section id="models" className="py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <InView variants={fadeUp(0)} className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-widest text-cyan-400/75">Models</p>
            <h2 className="text-[clamp(1.6rem,3vw,2.4rem)] font-semibold leading-[1.15] tracking-[-0.03em] text-white">
              Open Weight &amp; Open Source
            </h2>
          </div>
          <a href="#all-models"
            className="hidden shrink-0 items-center gap-1.5 text-sm font-medium text-white/45 transition hover:text-white/75 lg:flex">
            View all models <Icon name="arrow-right" className="w-4 h-4" />
          </a>
        </InView>

        <motion.div
          className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-5"
          variants={stagger} initial="hidden"
          whileInView="visible" viewport={{ once: true, amount: 0.08 }}>
          {models.map((model) => (
            <motion.article key={model.name}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-[#111127]/50 p-4.5 cursor-pointer transition-all duration-300"
              variants={cardAnim}
              whileHover={{ y: -5, borderColor: `${model.accent}28` }}
              transition={{ duration: 0.2 }}>
              {/* Accent glow */}
              <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 rounded-2xl"
                style={{ background: `radial-gradient(circle at 0% 0%, ${model.accent}16 0%, transparent 55%)` }} />

              <div className="relative z-10 mb-3 flex items-start justify-between gap-2">
                <ModelLogo model={model} />
                {model.badge && (
                  <span className="rounded-md border border-white/[0.07] bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/55">
                    {model.badge}
                  </span>
                )}
              </div>

              <div className="relative z-10 flex flex-col flex-1">
                <h3 className="mb-0.5 text-sm font-semibold text-white">{model.name}</h3>
                <p className="mb-2.5 text-[11px] font-medium text-white/30">{model.family}</p>
                <p className="mb-3.5 flex-1 text-xs text-white/40 leading-relaxed">{model.detail}</p>
                <div className="flex flex-wrap gap-1">
                  {model.chips.map((chip) => (
                    <span key={chip} className="rounded bg-white/[0.05] px-2 py-0.5 text-[10px] text-white/45">{chip}</span>
                  ))}
                </div>
              </div>

              <div className="relative z-10 mt-3.5 flex justify-end">
                <Icon name="arrow-right" className="w-3.5 h-3.5 text-white/18 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-white/45" />
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── IDE Compatibility Section ────────────────────────────────────────────────
function CompatibilitySection() {
  const allTools = [
    // Row 1 — AI coding tools (with logos from dev-content)
    { name: "OpenCode",   logo: opencodeSvg,  bg: "#18181b", color: "#fff",     desc: "AI coding agent",       category: "IDE Tool" },
    { name: "OpenClaw",   logo: openclawSvg,  bg: "#1a0505", color: "#FF4D4D",  desc: "AI pair programmer",    category: "IDE Tool" },
    { name: "Kilo Code",  logo: kilocodeSvg,  bg: "#18181b", color: "#fff",     desc: "Code generation IDE",   category: "IDE Tool" },
    { name: "Open WebUI", logo: openwebuiSvg, bg: "#18181b", color: "#fff",     desc: "Self-hosted chat UI",   category: "Chat UI" },
    { name: "Z.ai",       logo: zaiSvg,       bg: "#18181b", color: "#fff",     desc: "AI workspace",          category: "Platform" },
    // Row 2 — OpenAI-compatible tools (inline icon)
    { name: "Cursor",     logo: null,         bg: "#18181b", color: "#fff",     desc: "AI code editor",        category: "IDE" },
    { name: "Continue",   logo: null,         bg: "#18181b", color: "#4ade80",  desc: "Open-source copilot",   category: "IDE Plugin" },
    { name: "Aider",      logo: null,         bg: "#18181b", color: "#a78bfa",  desc: "AI pair programming",   category: "Terminal" },
    { name: "LM Studio",  logo: null,         bg: "#18181b", color: "#f59e0b",  desc: "Local LLM runner",      category: "Desktop" },
    { name: "Jan",        logo: null,         bg: "#18181b", color: "#06b6d4",  desc: "Open-source ChatGPT",   category: "Desktop" },
  ];

  return (
    <section className="py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        {/* Header */}
        <InView variants={fadeUp(0)} className="mb-12 text-center">
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-widest text-cyan-400/75">
            Works Everywhere
          </p>
          <h2 className="text-[clamp(1.6rem,3vw,2.4rem)] font-semibold leading-[1.15] tracking-[-0.03em] text-white">
            Compatible with your favourite tools
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-white/40 leading-relaxed">
            Krato AI's OpenAI-compatible API works out-of-the-box with every major AI coding tool,
            chat interface, and platform — just swap your base URL.
          </p>
        </InView>

        {/* Badge */}
        <InView variants={fadeUp(0.05)} className="mb-8 flex justify-center">
          <div className="inline-flex items-center gap-2.5 rounded-xl border border-cyan-400/15 bg-cyan-400/[0.06] px-4 py-2.5 text-sm">
            <Icon name="code2" className="w-4 h-4 text-cyan-400" />
            <span className="text-white/60">Change only one line —</span>
            <code className="rounded bg-white/[0.07] px-2 py-0.5 text-xs text-cyan-300 font-mono">
              base_url="https://api.krato.ai/v1"
            </code>
          </div>
        </InView>

        {/* Tool grid */}
        <motion.div
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
          variants={stagger} initial="hidden"
          whileInView="visible" viewport={{ once: true, amount: 0.08 }}>
          {allTools.map((tool) => (
            <motion.div key={tool.name}
              className="compat-card group relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#111127]/50 p-4 cursor-pointer transition-all duration-300 hover:border-white/[0.14] hover:bg-[#111127]/80"
              variants={cardAnim}
              whileHover={{ y: -4, scale: 1.01 }}
              transition={{ duration: 0.18 }}>

              {/* Category pill */}
              <span className="mb-3 inline-block rounded-md bg-white/[0.05] px-2 py-0.5 text-[10px] font-medium text-white/35">
                {tool.category}
              </span>

              {/* Logo / Icon */}
              <div className="mb-3 flex h-10 w-10 items-center justify-center">
                {tool.logo ? (
                  <img
                    src={tool.logo}
                    alt={tool.name}
                    className="h-9 w-9 object-contain"
                    style={{ filter: tool.color === "#fff" ? "brightness(0) invert(1)" : "none" }}
                  />
                ) : (
                  /* Fallback: first-letter monogram */
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.05] text-base font-bold"
                    style={{ color: tool.color }}>
                    {tool.name.charAt(0)}
                  </div>
                )}
              </div>

              <h3 className="text-sm font-semibold text-white mb-1">{tool.name}</h3>
              <p className="text-[11px] text-white/38 leading-relaxed">{tool.desc}</p>

              {/* Checkmark */}
              <div className="absolute top-3.5 right-3.5 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400/[0.1] opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <Icon name="check" className="w-3 h-3 text-cyan-400" />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom note */}
        <InView variants={fadeUp(0.05)} className="mt-8 text-center">
          <p className="text-xs text-white/30">
            Any tool that supports OpenAI's API spec works with Krato AI — including LangChain, LlamaIndex, Vercel AI SDK, and more.
          </p>
        </InView>
      </div>
    </section>
  );
}

// ─── Stats Section ────────────────────────────────────────────────────────────
function StatsSection() {
  return (
    <section className="py-14 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <InView variants={fadeUp(0)}>
          <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-[#111127]/55 backdrop-blur-sm">
            {/* Desktop */}
            <motion.div
              className="hidden lg:grid grid-cols-6 divide-x divide-white/[0.06]"
              variants={stagger} initial="hidden"
              whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
              {stats.map((stat) => (
                <motion.div key={stat.label} className="flex flex-col items-start gap-3.5 px-6 py-7" variants={cardAnim}>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.07] bg-white/[0.04] text-cyan-400">
                    <Icon name={stat.icon} className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[1.75rem] font-black leading-none tracking-[-0.04em] text-white">{stat.value}</p>
                    <p className="mt-1.5 text-[11px] text-white/38">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Mobile */}
            <motion.div
              className="grid grid-cols-2 lg:hidden"
              variants={stagger} initial="hidden"
              whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
              {mobileStats.map((stat, i) => (
                <motion.div key={stat.label}
                  className={`flex flex-col gap-3 px-5 py-6 ${i % 2 !== 0 ? "border-l border-white/[0.06]" : ""} ${i >= 2 ? "border-t border-white/[0.06]" : ""}`}
                  variants={cardAnim}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.04] text-cyan-400">
                    <Icon name={stat.icon} className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <p className="text-xl font-black leading-none tracking-[-0.04em] text-white">{stat.value}</p>
                    <p className="mt-1 text-[11px] text-white/38">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </InView>
      </div>
    </section>
  );
}

// ─── CTA Section ──────────────────────────────────────────────────────────────
function CTASection() {
  return (
    <section className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <InView variants={fadeUp(0)}>
          <div className="cta-card relative overflow-hidden rounded-3xl border border-white/[0.07] p-10 text-center lg:p-14">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute left-1/2 top-0 h-px w-1/2 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              <div className="absolute left-1/2 top-0 h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-500/[0.12] blur-3xl" />
            </div>
            <div className="relative z-10">
              <p className="mb-3.5 text-[11px] font-semibold uppercase tracking-widest text-cyan-400/75">Get Started Today</p>
              <h2 className="mb-4 text-[clamp(1.8rem,3.5vw,3rem)] font-semibold leading-[1.1] tracking-[-0.04em] text-white">
                The fastest way to run<br />open models in India
              </h2>
              <p className="mx-auto mb-8 max-w-md text-sm text-white/40 leading-relaxed">
                Join thousands of developers building with open-weight models on Krato AI. No international credit card required.
              </p>
              <div className="flex flex-col items-center justify-center gap-2.5 sm:flex-row">
                <a href="#start"
                  className="hero-primary-btn inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-white transition-all">
                  Start Building Free
                  <Icon name="arrow-right" className="w-4 h-4" />
                </a>
                <a href="#contact"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] bg-white/[0.03] px-6 py-3 text-sm font-medium text-white/65 transition-all hover:border-white/20 hover:bg-white/[0.06] hover:text-white">
                  Talk to Sales
                </a>
              </div>
              <p className="mt-5 text-[11px] text-white/25">No credit card required · Pay in INR · Cancel anytime</p>
            </div>
          </div>
        </InView>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Platform",   links: ["Inference", "Fine-tuning", "GPU Clusters", "RAG", "Embeddings"] },
    { title: "Models",     links: ["DeepSeek", "Gemma", "Qwen", "GLM", "All Models"] },
    { title: "Developers", links: ["Documentation", "API Reference", "Changelog", "Status", "GitHub"] },
    { title: "Company",    links: ["About", "Blog", "Careers", "Contact", "Privacy"] },
  ];
  return (
    <footer className="border-t border-white/[0.05] bg-[#0a0a19] py-14 lg:py-18">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_repeat(4,1fr)]">
          <div>
            <BrandMark />
            <p className="mt-3.5 max-w-[200px] text-xs text-white/30 leading-relaxed">
              Open AI infrastructure powered by open source. Built in India, for the world.
            </p>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <p className="mb-3.5 text-[10px] font-semibold uppercase tracking-widest text-white/28">{col.title}</p>
              <ul className="flex flex-col gap-2.5">
                {col.links.map((link) => (
                  <li key={link}>
                    <a href="#" className="text-xs text-white/40 transition hover:text-white/70">{link}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/[0.05] pt-7 sm:flex-row">
          <p className="text-[11px] text-white/25">© 2024 Krato AI. All rights reserved.</p>
          <p className="text-[11px] text-white/25">Designed for India's AI Future · <span className="text-white/40">Pay in INR via UPI</span></p>
        </div>
      </div>
    </footer>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export function App() {
  const isSlidesPage = window.location.pathname === "/slides";

  if (isSlidesPage) {
    return <SlidesPage />;
  }

  return (
    <div className="app-root min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 -z-10 bg-[#0a0a19]" />
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(99,102,241,0.11),transparent)]" />

      <Navbar />
      <AnnouncementBar />
      <Hero />
      <PricingBar />
      <FeaturesSection />
      <ModelsSection />
      <CompatibilitySection />
      <StatsSection />
      <CTASection />
      <Footer />
    </div>
  );
}

export default App;
